import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPause, faPlay } from "@fortawesome/free-solid-svg-icons";
import AudioManager from "@/utils/audioManager";

interface MiniAudioBarProps {
  messageId: number;
  isDarkMode: boolean;
  title?: string;
}

export const MiniAudioBar: React.FC<MiniAudioBarProps> = ({ messageId, isDarkMode, title }) => {
  const audioManager = useMemo(() => AudioManager.getInstance(), []);
  const [isActiveFromManager, setIsActiveFromManager] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  // Linger visibility and fade-out control after audio completes
  const [lingerVisible, setLingerVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const fadeTimeoutRef = React.useRef<number | null>(null);
  // Show instantly when ChatMessage dispatches a 'preparing' event
  const [preparingVisible, setPreparingVisible] = useState(false);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const updateFromManager = () => {
      const active = audioManager.getCurrentMessageId() === messageId && !!audioManager.getCurrentAudio();
      setIsActiveFromManager(active);
      const el = audioManager.getCurrentAudio();
      if (active && el) {
        setIsPlaying(!el.paused);
        setDuration(el.duration || 0);
        setCurrentTime(el.currentTime || 0);
      } else {
        setIsPlaying(false);
        setDuration(0);
        setCurrentTime(0);
      }
    };

    // Initial sync
    updateFromManager();

    const onTick = (e: Event) => {
      const detail = (e as CustomEvent).detail as any;
      if (!detail) return;
      if (detail.messageId === messageId || audioManager.getCurrentMessageId() === messageId) {
        updateFromManager();
        if (detail.action === 'preparing' && detail.messageId === messageId) {
          // Show immediately with spinner until audio starts
          setPreparingVisible(true);
          // Cancel any fade if user triggered new playback
          if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
          setLingerVisible(false);
          setFadeOut(false);
        }
        // Handle fade-out on end for this message
        if (detail.action === 'ended' && detail.messageId === messageId) {
          // Keep visible and fade out for 3 seconds
          setLingerVisible(true);
          setFadeOut(true);
          if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
          fadeTimeoutRef.current = window.setTimeout(() => {
            setLingerVisible(false);
            setFadeOut(false);
          }, 400) as unknown as number;
        }
        if (detail.action === 'play' || detail.action === 'resume') {
          // Cancel lingering if new play starts for this message
          if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
          setLingerVisible(false);
          setFadeOut(false);
          setPreparingVisible(false);
        }
        if (detail.action === 'stop' || detail.action === 'error' || detail.action === 'pause') {
          // No need to keep preparing state on stop/error/pause
          setPreparingVisible(false);
        }
      }
    };

    window.addEventListener("audioManagerUpdate", onTick as EventListener);

    // Also poll time a bit in case of no events during seeking
    const interval = window.setInterval(() => {
      const el = audioManager.getCurrentAudio();
      if (audioManager.getCurrentMessageId() === messageId && el) {
        setCurrentTime(el.currentTime || 0);
        setDuration(el.duration || 0);
      }
    }, 250);

    return () => {
      window.removeEventListener("audioManagerUpdate", onTick as EventListener);
      window.clearInterval(interval);
      if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    };
  }, [audioManager, messageId]);

  const shouldShow = isActiveFromManager || lingerVisible || preparingVisible;
  if (!shouldShow) return null;

  const el = audioManager.getCurrentAudio();

  return (
    <div className={`mt-2 w-full transition-opacity px-2 max-w-[500px] duration-500 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
      <div className={`rounded-full max-w-[450px] pl-3 pr-6 py-2 ${isDarkMode ? 'bg-[#1c1c1c] text-white' : 'bg-gray-100 text-gray-900'}` }>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full"
            onClick={() => {
              if (!el) return;
              if (isPlaying) {
                audioManager.pauseAudio();
              } else {
                audioManager.resumeAudio();
              }
            }}
            aria-label={isPlaying ? "Pause" : "Play"}
            disabled={preparingVisible}
          >
            {preparingVisible ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : el ? (
              isPlaying ? (
                <FontAwesomeIcon icon={faPause} className="w-5 h-5" />
              ) : (
                <FontAwesomeIcon icon={faPlay} className="w-5 h-5" />
              )
            ) : (
              <Loader2 className="h-5 w-5 animate-spin" />
            )}
          </Button>

          {preparingVisible ? null : title ? (
            <div className="hidden sm:block max-w-[40%] truncate text-sm opacity-90">{title}</div>
          ) : null}

          <div className="flex-1 min-w-0">
            <div className={`relative h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}>
              <div
                className={`absolute left-0 top-0 h-full rounded-full ${isDarkMode ? 'bg-white' : 'bg-black'}`}
                style={{ width: `${(currentTime / (duration || 1)) * 100}%`, transition: 'width 0.1s linear' }}
              />
              <input
                type="range"
                min={0}
                max={duration || 0}
                value={currentTime}
                onChange={(e) => {
                  if (el) {
                    const value = Number(e.target.value);
                    el.currentTime = value;
                    setCurrentTime(value);
                  }
                }}
                disabled={preparingVisible}
                className={`absolute inset-0 w-full h-full opacity-0 ${preparingVisible ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                aria-label="Seek"
              />
            </div>
            <div className="flex justify-between mt-1 text-[11px] leading-none opacity-80">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniAudioBar;
